import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaArrowTrendUp,
  FaClipboardList,
  FaCircleCheck,
} from "react-icons/fa6";

const StatsCard = ({
  title,
  total,
  daily,
  weekly,
  monthly,
  icon,
  gradient,
}) => {
  return (
    <div
      className={`
        relative overflow-hidden
        rounded-3xl
        p-6
        shadow-lg
        hover:shadow-2xl
        hover:-translate-y-2
        transition-all
        duration-300
        ${gradient}
      `}
    >
      {/* Decorative circles */}
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10"></div>
      <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/10"></div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-white/80 text-sm uppercase tracking-widest">
              {title}
            </p>

            <h2 className="text-4xl font-bold text-white mt-2">
              {total || 0}
            </h2>
          </div>

          <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-2xl">
            {icon}
          </div>
        </div>

        {/* Trend */}
        <div className="flex items-center gap-2 mt-4 mb-5">
          <div className="flex items-center gap-1 bg-green-500 px-3 py-1 rounded-full text-xs text-white">
            <FaArrowTrendUp />
            Growth
          </div>

          <span className="text-white/70 text-sm">
            Current Statistics
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center">
            <p className="text-xs text-white/70">Today</p>
            <p className="text-lg font-semibold text-white">
              {daily || 0}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center">
            <p className="text-xs text-white/70">Weekly</p>
            <p className="text-lg font-semibold text-white">
              {weekly || 0}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center">
            <p className="text-xs text-white/70">Monthly</p>
            <p className="text-lg font-semibold text-white">
              {monthly || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingCard = () => {
  return (
    <div className="h-72 rounded-3xl bg-white shadow-md animate-pulse">
      <div className="p-6">
        <div className="h-4 w-32 bg-gray-300 rounded mb-4"></div>
        <div className="h-10 w-20 bg-gray-300 rounded mb-6"></div>

        <div className="space-y-3">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );
};

const Index = () => {
  const [dailyData, setDailyData] = useState({});
  const [resolvedData, setResolvedData] = useState({});
  const [notAssignedData, setNotAssignedData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/countComplaint`
        );

        setDailyData(data?.message?.[0] || {});
        setResolvedData(data?.resolved?.[0] || {});
        setNotAssignedData(data?.notAssigned?.[0] || {});
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-100 via-white to-slate-200 p-4 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
          Complaint Dashboard
        </h1>

        <p className="text-slate-500 mt-2">
          Monitor complaints and resolution performance.
        </p>
      </div>

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {[...Array(3)].map((_, index) => (
            <LoadingCard key={index} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatsCard
            title="Total Complaints"
            total={dailyData?.totalComplaint}
            daily={dailyData?.daily}
            weekly={dailyData?.weekly}
            monthly={dailyData?.monthly}
            icon={<FaClipboardList />}
            gradient="bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500"
          />

          <StatsCard
            title="Resolved Complaints"
            total={resolvedData?.total}
            daily={resolvedData?.daily}
            weekly={resolvedData?.weekly}
            monthly={resolvedData?.monthly}
            icon={<FaCircleCheck />}
            gradient="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700"
          />

          <StatsCard
            title="Not Assigned Complaints"
            total={notAssignedData?.total}
            daily={notAssignedData?.daily}
            weekly={notAssignedData?.weekly}
            monthly={notAssignedData?.monthly}
            icon={<FaCircleCheck />}
            gradient="bg-gradient-to-br from-red-500 via-rose-600 to-red-700"
          />

          {/* Dummy cards for future data */}

          {/* <StatsCard
            title="Pending Complaints"
            total={0}
            daily={0}
            weekly={0}
            monthly={0}
            icon={<FaClipboardList />}
            gradient="bg-gradient-to-br from-rose-500 via-pink-600 to-red-700"
          />

          <StatsCard
            title="Closed Cases"
            total={0}
            daily={0}
            weekly={0}
            monthly={0}
            icon={<FaCircleCheck />}
            gradient="bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700"
          /> */}
        </div>
      )}
    </div>
  );
};

export default Index;